import { Button } from '@1trading/react/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@1trading/react/components/ui/form';
import { Input } from '@1trading/react/components/ui/input';
import { Loading } from '@1trading/react/components/ui/loading';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@1trading/react/components/ui/select';
import { useToast } from '@1trading/react/hooks/use-toast';
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { HTTPError } from 'ky';
import { Info } from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Navigate, type To, useNavigate } from 'react-router-dom';

import { Alert, AlertDescription } from '@/components/ui/alert';
import LoadingCard from '@/components/ui/loading-card';
import { PageCard, PageCardContent, PageCardFooter } from '@/components/ui/page-card';
import { PageHeading, PageHeadingDescription, PageHeadingTitle } from '@/components/ui/page-heading';
import { NONE_VALUE } from '@/constants/answers';
import { useIdentity } from '@/features/onboarding/personal-information/hooks/use-identity';
import PeriodicReviewGoToSummaryButton from '@/features/periodic-review/components/go-to-summary-button';
import { useIsPeriodicReview } from '@/features/periodic-review/context';
import { useNationalClientIdentifier, useNationalClientIdentifierMutation } from '@/features/verification/hooks/use-national-client-identifier';
import { useMainstreamCountries } from '@/hooks/queries/use-mainstream-countries';
import { useNationalClientIdentifierFormats } from '@/hooks/queries/use-national-client-identifier-formats';
import type { NationalClientIdentifierPostErrorResponse } from '@/services/api/profile/national-client-identifier';
import { getNationalClientIdentifierExample, reportNationalClientIdentifierFormatError } from '@/services/api/profile/national-client-identifier-format';
import {
  appendNationalClientIdentifierExample,
  getNationalClientIdentifierTypeLabel,
  getPriorityCountryForCitizenships,
  isConcatOnlyNationalClientIdentifier,
  NationalClientIdentifierJustificationReason,
  NationalClientIdentifierType,
} from '@/utils/national-client-identifier';
import { type VerificationInformationOrDefaults, type VerificationInformation as VerificationInformationType, verificationInformationSchema } from '../schemas/information';

type VerificationInformationProps = {
  next: To;
  prev?: To;
  summary?: To;
};

const VerificationInformationForm = ({
  defaultValues,
  onReturnToSummary,
  onSuccess,
}: {
  defaultValues: VerificationInformationOrDefaults;
  onReturnToSummary: VoidFunction;
  onSuccess: VoidFunction;
}) => {
  const { data: countries } = useMainstreamCountries();
  const { data: nationalClientIdentifierFormats } = useNationalClientIdentifierFormats();
  const form = useForm<VerificationInformationType>({
    defaultValues,
    resolver: standardSchemaResolver(verificationInformationSchema),
  });
  const { isPending, isSuccess, mutate } = useNationalClientIdentifierMutation();
  const { toast } = useToast();
  const { i18n, t } = useTranslation();
  const countryAlpha2 = defaultValues.country;
  const country = countries?.[countryAlpha2];
  if (!country) throw new Error('Country not found');
  const nciPriority = country.nciPriority;
  const identifierType = form.watch('identifierType');
  const identifierTypePriorityIndex = identifierType && identifierType !== NONE_VALUE ? nciPriority.indexOf(identifierType) : -1;
  const isConcatOnly = isConcatOnlyNationalClientIdentifier(nciPriority);

  const onError = useCallback(() => {
    toast({
      description: t('fundamentals.defaultError.message'),
      variant: 'destructive',
    });
  }, [t, toast]);

  const onSubmit = useCallback(
    (data: VerificationInformationType, { isReturningToSummary = false }: { isReturningToSummary?: boolean } = {}) => {
      mutate(
        { answers: data },
        {
          onError: async (error) => {
            if (error instanceof HTTPError) {
              const body = await error.response.json<NationalClientIdentifierPostErrorResponse>().catch(() => null);
              if (body?.error) {
                const fieldName = body.error.match(/^answers\.(\w+):/)?.[1];
                if (fieldName && i18n.exists(`nationalClientIdentifier.${fieldName}.errors.invalid`)) {
                  const submittedType = form.getValues('identifierType');
                  form.setError(fieldName as keyof VerificationInformationType, {
                    message: appendNationalClientIdentifierExample(
                      i18n.t(`nationalClientIdentifier.${fieldName}.errors.invalid`, {
                        type: getNationalClientIdentifierTypeLabel(submittedType, countryAlpha2),
                      }),
                      getNationalClientIdentifierExample(nationalClientIdentifierFormats, { country: countryAlpha2, identifierType: submittedType }),
                    ),
                  });
                  return;
                }
              }
            }

            onError();
          },
          onSuccess: () => (isReturningToSummary ? onReturnToSummary() : onSuccess()),
        },
      );
    },
    [countryAlpha2, form.getValues, form.setError, i18n.exists, i18n.t, mutate, nationalClientIdentifierFormats, onError, onReturnToSummary, onSuccess],
  );

  const onInvalid = useCallback(() => reportNationalClientIdentifierFormatError(form.getValues()), [form.getValues]);

  useEffect(() => {
    if (!isConcatOnly) return;

    mutate(
      {
        answers: {
          country: countryAlpha2,
          identifierType: NationalClientIdentifierType.CONCAT,
        },
      },
      {
        onError,
        onSuccess,
      },
    );
  }, [countryAlpha2, isConcatOnly, mutate, onError, onSuccess]);

  if (isConcatOnly) {
    return (
      <PageCard>
        <LoadingCard />
      </PageCard>
    );
  }

  return (
    <Form {...form}>
      <div className="relative flex flex-row justify-end">
        <PeriodicReviewGoToSummaryButton disabled={isPending} onSubmit={() => form.handleSubmit((data) => onSubmit(data, { isReturningToSummary: true }))()} />
      </div>
      <PageCard>
        <form name="verification-information" onSubmit={form.handleSubmit((data) => onSubmit(data), onInvalid)}>
          <PageCardContent className="pt-8 space-y-6">
            <div className="rounded-md p-4 space-y-4 bg-background">
              <p>{t('verificationInformation.body.line1')}</p>
              <p>
                <Trans i18nKey="verificationInformation.body.line2" values={{ country: country.name }} components={[<strong key="country" />]} />
              </p>
              <p>{t('verificationInformation.body.line3')}</p>
            </div>
            <FormField
              name="identifierType"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('nationalClientIdentifier.type.title')}</FormLabel>
                  <FormControl>
                    <Select
                      value={field.value}
                      onValueChange={(value: NationalClientIdentifierType | typeof NONE_VALUE) => {
                        field.onChange(value);
                        form.setValue('identifier', '');

                        const nextPriorityIndex = value !== NONE_VALUE ? nciPriority.indexOf(value) : -1;
                        if (nextPriorityIndex < 1) form.setValue('firstPriorityJustification', '' as NationalClientIdentifierJustificationReason);
                        if (nextPriorityIndex < 2) form.setValue('secondPriorityJustification', '' as NationalClientIdentifierJustificationReason);
                      }}
                    >
                      <FormControl>
                        <SelectTrigger value={field.value}>
                          <SelectValue placeholder={t('nationalClientIdentifier.type.placeholder')} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {[...nciPriority, ...(nciPriority.includes(NationalClientIdentifierType.CONCAT) ? [] : [NONE_VALUE])].map((priority) => (
                          <SelectItem key={priority} value={priority}>
                            {getNationalClientIdentifierTypeLabel(priority, countryAlpha2)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {identifierType && identifierType !== NONE_VALUE && identifierType !== NationalClientIdentifierType.CONCAT ? (
              <FormField
                name="identifier"
                control={form.control}
                render={({ field: { value, ...field } }) => (
                  <FormItem>
                    <FormLabel>{getNationalClientIdentifierTypeLabel(identifierType, countryAlpha2)}</FormLabel>
                    <FormControl>
                      <Input
                        value={value ?? ''}
                        {...field}
                        placeholder={appendNationalClientIdentifierExample(
                          t(`nationalClientIdentifier.type.options.${identifierType}.placeholder`, {
                            type: getNationalClientIdentifierTypeLabel(identifierType, countryAlpha2),
                          }),
                          getNationalClientIdentifierExample(nationalClientIdentifierFormats, { country: countryAlpha2, identifierType }),
                        )}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : null}
            {identifierTypePriorityIndex > 0 ? (
              <>
                <FormField
                  name="firstPriorityJustification"
                  control={form.control}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t('nationalClientIdentifier.justification.title', {
                          type: nciPriority[0] ? getNationalClientIdentifierTypeLabel(nciPriority[0], countryAlpha2) : undefined,
                        })}
                      </FormLabel>
                      <Select value={field.value ?? ''} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger value={field.value}>
                            <SelectValue placeholder={t('nationalClientIdentifier.justification.placeholder')} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {Object.values(NationalClientIdentifierJustificationReason).map((answer) => (
                            <SelectItem key={answer} value={answer}>
                              {t(`nationalClientIdentifier.justification.options.${answer}`)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {identifierTypePriorityIndex > 1 ? (
                  <FormField
                    name="secondPriorityJustification"
                    control={form.control}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {t('nationalClientIdentifier.justification.title', {
                            type: nciPriority[1] ? getNationalClientIdentifierTypeLabel(nciPriority[1], countryAlpha2) : undefined,
                          })}
                        </FormLabel>
                        <Select value={field.value ?? ''} onValueChange={field.onChange}>
                          <FormControl>
                            <SelectTrigger value={field.value}>
                              <SelectValue placeholder={t('nationalClientIdentifier.justification.placeholder')} />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {Object.values(NationalClientIdentifierJustificationReason).map((answer) => (
                              <SelectItem key={answer} value={answer}>
                                {t(`nationalClientIdentifier.justification.options.${answer}`)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                ) : null}
              </>
            ) : null}
            {identifierType === NONE_VALUE ? (
              <Alert variant="info" role="status">
                <Info aria-hidden className="size-4" />
                <AlertDescription>{t('nationalClientIdentifier.restricted')}</AlertDescription>
              </Alert>
            ) : null}
          </PageCardContent>
          <PageCardFooter>
            <Button className="relative w-full" size="lg" type="submit" variant="primary" disabled={isPending || isSuccess}>
              <span className={isPending || isSuccess ? 'invisible' : ''}>{t('buttons.continue')}</span>
              {isPending ? <Loading variant="inverted" position="overlay" size="sm" /> : null}
            </Button>
          </PageCardFooter>
        </form>
      </PageCard>
    </Form>
  );
};
VerificationInformationForm.displayName = 'VerificationInformationForm';

const VerificationInformationContent = ({ children }: React.PropsWithChildren) => {
  const { t } = useTranslation();

  return (
    <>
      <PageHeading>
        <PageHeadingTitle>{t('verificationInformation.title')}</PageHeadingTitle>
        <PageHeadingDescription>{t('verificationInformation.description')}</PageHeadingDescription>
      </PageHeading>
      {children}
    </>
  );
};
VerificationInformationContent.displayName = 'VerificationInformationContent';

const VerificationInformation = ({ next, prev, summary }: VerificationInformationProps) => {
  const isPeriodicReview = useIsPeriodicReview();
  const { isLoading: isLoadingCountries, data: countries } = useMainstreamCountries();
  const { isComplete: isCompleteIdentity, isLoading: isLoadingIdentity, data: identity } = useIdentity();
  const { isLoading: isLoadingNationalClientIdentifier, data: nationalClientIdentifier } = useNationalClientIdentifier();
  const navigate = useNavigate();
  const isLoading = isLoadingCountries || isLoadingIdentity || isLoadingNationalClientIdentifier;

  const handleSuccess = useCallback(async () => {
    navigate(next, { replace: true });
  }, [navigate, next]);

  const handleReturnToSummary = useCallback(() => {
    navigate(summary ?? next, { replace: true });
  }, [navigate, next, summary]);

  if (isLoading) return <LoadingCard />;

  if (!identity || !isCompleteIdentity) {
    if (prev) return <Navigate to={prev} replace />;

    throw new Error('Identity not found with no previous page specified');
  }

  const country = getPriorityCountryForCitizenships(identity.citizenship, identity.citizenship2, countries ?? {}) ?? '';
  // A review can move the customer to another country, whose identifiers are different
  const isOtherCountryIdentifier = isPeriodicReview && nationalClientIdentifier?.answers.country !== country;
  const defaultValues: VerificationInformationOrDefaults = {
    ...(isOtherCountryIdentifier ? {} : nationalClientIdentifier?.answers),
    country,
  };

  return (
    <VerificationInformationContent>
      <VerificationInformationForm defaultValues={defaultValues} onReturnToSummary={handleReturnToSummary} onSuccess={handleSuccess} />
    </VerificationInformationContent>
  );
};
VerificationInformation.displayName = 'VerificationInformation';

export default VerificationInformation;
